import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  FormControl,
  FormGroup,
  Validators,
  ValidationErrors,
  AsyncValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { InvitationAccessLevel } from '@vi-iot/client';

import { CommercialInvitationAccessLevel, CommercialStore, ResponseStatus } from '@store';

import { PATTERN } from '../../../../shared/common';
import { PillType } from '../../../../shared/components/pill/pill.model';

import { PartnerAccessInvitationFormControls } from './partner-access-share-device-modal.helper';

@Component({
  selector: 'app-partner-access-share-device-modal',
  templateUrl: './partner-access-share-device-modal.component.html',
  styleUrls: ['./partner-access-share-device-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerAccessShareDeviceModalComponent implements OnInit {
  readonly InvitationAccessLevel = InvitationAccessLevel;
  readonly ResponseStatus = ResponseStatus;

  readonly pillType = PillType;
  formGroup = new FormGroup<PartnerAccessInvitationFormControls>({
    accessLevel: new FormControl<CommercialInvitationAccessLevel>(null, [Validators.required]),
    inviteeEmail: new FormControl<string>(
      '',
      [Validators.required, Validators.pattern(PATTERN.EMAIL)],
      [this.isEmailNotAdded(), this.isEmailAlreadyInForm()]
    ),
  });

  constructor(
    public dialogRef: MatDialogRef<PartnerAccessShareDeviceModalComponent>,
    public commercialStore: CommercialStore
  ) {}

  private isEmailNotAdded(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return this.commercialStore.isEmailAlreadyInvited(control.value).pipe(
        map((isEmailAlreadyInvited) =>
          isEmailAlreadyInvited ? { emailAlreadyInvited: true } : null
        ),
        take(1)
      );
    };
  }

  private isEmailAlreadyInForm(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return this.commercialStore.isEmailAlreadyInForm(control.value).pipe(
        map((isEmailAlreadyInForm) => (isEmailAlreadyInForm ? { emailAlreadyInForm: true } : null)),
        take(1)
      );
    };
  }

  ngOnInit(): void {
    this.commercialStore.clearPartnerFromForm();
  }

  addParticipant(): void {
    this.commercialStore.addPartnerToForm(this.formGroup.getRawValue());
    this.formGroup.reset();
  }

  removeParticipant(index: number): void {
    this.commercialStore.removePartnerFromForm(index);
  }

  sendInvitations(): void {
    this.commercialStore.sendInvitationsForm();
  }

  sendSpecificInvitation(index: number): void {
    this.commercialStore.sendSpecificInvitation(index);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getAccessLevelTranslation(accessLevel: CommercialInvitationAccessLevel): string {
    switch (accessLevel) {
      case InvitationAccessLevel.Partner:
        return 'INSTALLATIONS.INSTALLATION.PARTNER_ACCESS_MODAL.COMPANY';
      case InvitationAccessLevel.Consumer:
        return 'INSTALLATIONS.INSTALLATION.PARTNER_ACCESS_MODAL.END_CUSTOMER';
    }
  }
}
